<template>
  <div class="scroll">
    <v-data-table
      :headers="headers"
      :items="items"
      :disable-initial-sort="true"
      :rows-per-page-items="[-1]"
      :loading="loading"
      hide-actions
      class="elevation-1"
    >
      <template #headers="props">
        <th
          v-for="header in props.headers"
          :key="header.text"
          :class="[
            'column sortable px-2 py-0',
            sortOptions.descending ? 'desc' : 'asc',
            header.value === sortOptions.sortBy ? 'active' : '',
            header.align === 'center' ? 'text-xs-center' : '',
            header.align === 'right' ? 'text-xs-right' : '',
            header.align === 'left' ? 'text-xs-left' : '',
          ]"
          :aria-sort="header.sortable ? '' : 'none'"
          @click="changeSort(header)"
        >
          {{ header.text }}
          <v-icon v-if="header.sortable" small>arrow_upward</v-icon>
        </th>
      </template>

      <template slot="items" slot-scope="props">
        <tr :class="{ completed: props.item.isCompleted }">
          <td class="text-xs-left">
            <AppStatus :value="props.item.status" with-icon />
          </td>
          <td class="text-xs-right px-2 py-0 low-cell">
            <ServeStatusIndicator
              :value="props.item.serveStatus"
              class="mr-2"
            />
            <span class="percent">
              <AppPercent
                :value="props.item.yesterdayBudgetProgress * 100"
                integer
              />
            </span>
          </td>
          <td class="text-xs-left no-wrap px-2 py-0 low-cell fixed-width-cell">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <a @click="goToBudgetProgressReport(props.item)" v-on="on">{{
                  props.item.insertionOrder
                }}</a>
              </template>
              <span>{{ props.item.insertionOrder }}</span>
            </v-tooltip>
          </td>
          <td class="text-xs-left no-wrap px-2 py-0 low-cell fixed-width-cell">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <a @click="goToAdvertiserDashboard(props.item)" v-on="on">{{
                  props.item.advertiser
                }}</a>
              </template>
              <span>{{ props.item.advertiser }}</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center no-wrap px-2 py-0 low-cell">
            <AppDateTime :value="props.item.ioBudgetStartTime" is-date />
          </td>
          <td class="text-xs-center no-wrap px-2 py-0 low-cell">
            <AppDateTime :value="props.item.ioBudgetEndTime" is-date />
          </td>
          <td
            :class="[
              'text-xs-right px-2 py-0 low-cell',
              { lastMinute: props.item.isLastMinute },
            ]"
          >
            {{ props.item.remainingDays }}日
          </td>
          <td class="text-xs-right px-2 py-0 low-cell">
            <AppCurrency :value="props.item.remainingBudget" />
          </td>
          <td class="text-xs-right px-2 py-0 low-cell">
            <AppCurrency :value="props.item.yesterdayBuyingCurrency" />
          </td>
          <td class="text-xs-right px-2 py-0 low-cell">
            <AppCurrency :value="props.item.dailyBuyingCurrencyPlan" />
          </td>
        </tr>
      </template>
      <template #no-data>
        <div class="text-xs-center">データがありません。</div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { actions } from '@/vuex/modules/dashboard/constants'
import ServeStatusIndicator from './ServeStatusIndicator'

export default {
  name: 'IOTable',
  components: { ServeStatusIndicator },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    sortOptions: {
      type: Object,
      default: () => ({
        sortBy: 'remainingDays',
        descending: false,
      }),
    },
    loading: Boolean,
  },
  data() {
    return {
      headers: [
        {
          text: 'ステータス',
          value: 'status',
          align: 'center',
          class: 'px-2',
          sortable: false,
        },
        {
          text: '前日消化率',
          value: 'yesterdayBudgetProgress',
          align: 'center',
          width: '100px',
          class: 'px-2',
          sortable: true,
        },
        {
          text: '案件名',
          value: 'insertionOrderId',
          align: 'center',
          class: 'px-2',
          sortable: true,
        },
        {
          text: '広告主名',
          value: 'advertiserId',
          align: 'center',
          class: 'px-2',
          sortable: true,
        },
        {
          text: '開始日時',
          value: 'ioBudgetStartTime',
          align: 'center',
          class: 'px-2',
          sortable: true,
        },
        {
          text: '終了日時',
          value: 'ioBudgetEndTime',
          align: 'center',
          class: 'px-2',
          sortable: true,
        },
        {
          text: '残日数',
          value: 'remainingDays',
          align: 'center',
          class: 'px-2',
          sortable: true,
        },
        {
          text: '残予算',
          value: 'remainingBudget',
          align: 'center',
          class: 'px-2',
          sortable: true,
        },
        {
          text: '前日消化額',
          value: 'yesterdayBuyingCurrency',
          align: 'center',
          class: 'px-2',
          sortable: true,
        },
        {
          text: '1日必要額',
          value: 'dailyBuyingCurrencyPlan',
          align: 'center',
          class: 'px-2',
          sortable: true,
        },
      ],
    }
  },
  methods: {
    numberToPercent(number) {
      if (isNaN(number)) return '----'
      return new Intl.NumberFormat('ja-JP', { style: 'percent' }).format(number)
    },
    goToAdvertiserDashboard(report) {
      const { agencyId, advertiserId } = report
      this.$router
        .push(
          `/agencies/${agencyId}/advertisers/${advertiserId}/standard_reports`,
        )
        .catch(() => {})
    },
    goToBudgetProgressReport(report) {
      const { agencyId, advertiserId } = report
      this.$router
        .push(
          `/agencies/${agencyId}/advertisers/${advertiserId}/budget_progress_reports`,
        )
        .catch(() => {})
    },
    changeSort(header) {
      if (!header.sortable) return

      const column = header.value
      const newSortOptions = {
        ...this.sortOptions,
      }
      if (newSortOptions.sortBy === column) {
        newSortOptions.descending = !newSortOptions.descending
      } else {
        newSortOptions.sortBy = column
        newSortOptions.descending = false
      }
      this.$store.dispatch(actions.UPDATE_SORT_OPTIONS, newSortOptions)
    },
  },
}
</script>

<style scoped lang="scss">
.scroll {
  overflow-y: scroll;
  max-height: 700px;
}

.completed {
  td {
    color: #999999;
  }
}
.lastMinute {
  color: red;
}
.percent {
  display: inline-block;
  width: 30px;
}
.low-cell {
  height: 36px;
}
.fixed-width-cell {
  width: 200px;
  max-width: 200px;
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
}
</style>
